/**
 * Computes deep visual, density, and formatting metrics from resume data.
 * This function is used by the Quality Inspector, AI layer, and Renderer.
 */
export function calculateResumeMetrics(processedData, activeSections = [], pageCount = 1, densityMode = 'comfortable') {
  let wordCount = 0;
  let charCount = 0;
  let bulletCount = 0;
  let longestBulletLength = 0;
  let longestSkillLength = 0;

  const addText = (text) => {
    if (!text || typeof text !== 'string') return;
    charCount += text.length;
    wordCount += text.trim().split(/\s+/).filter(Boolean).length;
  };

  // 1. Personal Info
  if (processedData.personalInfo) {
    const info = processedData.personalInfo;
    addText(info.name);
    addText(info.roleTitle || info.role);
    addText(info.email);
    addText(info.phone);
    addText(info.location || info.city);
    if (info.links) {
      if (typeof info.links === 'object') {
        Object.values(info.links).forEach(addText);
      } else if (Array.isArray(info.links)) {
        info.links.forEach(addText);
      }
    }
  }

  // 2. Summary
  if (processedData.summary) {
    addText(processedData.summary);
  } else if (processedData.summaryText) {
    addText(processedData.summaryText);
  }

  // 3. Experience
  if (processedData.experience) {
    processedData.experience.forEach(job => {
      addText(job.role);
      addText(job.company);
      addText(job.startDate);
      addText(job.endDate);
      addText(job.location);
      if (job.bullets) {
        job.bullets.forEach(b => {
          bulletCount++;
          if (b.length > longestBulletLength) {
            longestBulletLength = b.length;
          }
          addText(b);
        });
      }
    });
  }

  // 4. Projects
  if (processedData.projects) {
    processedData.projects.forEach(proj => {
      addText(proj.name || proj.title);
      addText(proj.duration || proj.date);
      const tech = proj.techStack || proj.tech || proj.technologies;
      if (Array.isArray(tech)) {
        tech.forEach(addText);
      } else {
        addText(tech);
      }
      if (proj.bullets) {
        proj.bullets.forEach(b => {
          bulletCount++;
          if (b.length > longestBulletLength) {
            longestBulletLength = b.length;
          }
          addText(b);
        });
      }
    });
  }

  // 5. Education
  if (processedData.education) {
    processedData.education.forEach(edu => {
      addText(edu.school || edu.university);
      addText(edu.degree || edu.degreeName);
      addText(edu.duration || edu.date || edu.year);
      addText(edu.gpa || edu.cgpa);
    });
  }

  // 6. Skills
  if (processedData.skills) {
    processedData.skills.forEach(skill => {
      const name = typeof skill === 'string' ? skill : (skill.name || skill.title || '');
      if (name.length > longestSkillLength) {
        longestSkillLength = name.length;
      }
      addText(name);
    });
  }

  const readingTimeMinutes = Math.max(1, Math.ceil(wordCount / 200)); // ~200 WPM
  const overflowRisk = wordCount > 650 && pageCount === 1 ? 'High' : (wordCount > 480 && pageCount === 1 ? 'Medium' : 'Low');
  const atsRisk = activeSections.includes('languages') && activeSections.includes('skills') && pageCount > 2 ? 'Medium' : 'Low';
  const visualDensity = densityMode === 'comfortable' ? 'Comfortable' : (densityMode === 'standard' ? 'Standard' : 'Compact');

  return {
    words: wordCount,
    characters: charCount,
    bullets: bulletCount,
    pages: pageCount,
    sections: activeSections.length,
    longestBullet: longestBulletLength,
    longestSkill: longestSkillLength,
    readingTime: `${readingTimeMinutes} min`,
    overflowRisk,
    atsRisk,
    visualDensity
  };
}
export default calculateResumeMetrics;
